import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Loader2, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SUUBI_LOGO = "https://customer-assets.emergentagent.com/job_community-school-hub/artifacts/vxd2g9ou_suubi%20final_Logo_00_00.jpg";

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    const result = await login(email, password);
    
    if (result.success) {
      toast.success('Welcome back!');
      navigate('/dashboard');
    } else {
      toast.error(result.error);
    }
    
    setLoading(false);
  };

  const seedDemoData = async () => {
    setSeeding(true);
    try {
      await axios.post(`${API_URL}/api/seed-demo-data`);
      toast.success('Demo data created! Login with admin@suubi.org / admin123');
      setEmail('admin@suubi.org');
      setPassword('admin123');
    } catch (error) {
      if (error.response?.data?.message === 'Demo data already exists') {
        toast.info('Demo data already exists. Use admin@suubi.org / admin123');
        setEmail('admin@suubi.org');
        setPassword('admin123');
      } else {
        toast.error('Failed to seed demo data');
      }
    }
    setSeeding(false);
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left side - Hero */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#1B5E20] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1B5E20] to-[#2E7D32]" />
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1543689604-6fe8dbcd1f59?w=1200')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="relative z-10 p-12 flex flex-col justify-between h-full">
          {/* Logo Section */}
          <div className="flex items-center gap-4">
            <img 
              src={SUUBI_LOGO} 
              alt="Suubi Logo" 
              className="h-16 w-16 rounded-full object-cover bg-white shadow-lg"
            />
            <div>
              <span className="font-heading font-bold text-2xl text-white block">SUUBI</span>
              <span className="text-white/70 text-sm">Community Development Organisation</span>
            </div>
          </div>
          
          <div className="max-w-md">
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
              Nurturing Hope, Growing Community
            </h1>
            <p className="text-white/80 text-lg leading-relaxed">
              Empowering the Suubi community through transparent financial tracking, 
              staff management, and sustainable project development.
            </p>
          </div>

          <div className="flex gap-4 text-white/60 text-sm">
            <span>Income Tracking</span>
            <span>•</span>
            <span>Staff Performance</span>
            <span>•</span>
            <span>Project Management</span>
            <span>•</span>
            <span>Loan Program</span>
          </div>
        </div>
      </div>

      {/* Right side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="flex flex-col items-center gap-3 mb-8 lg:hidden">
            <img 
              src={SUUBI_LOGO} 
              alt="Suubi Logo" 
              className="h-20 w-20 rounded-full object-cover shadow-lg"
            />
            <div className="text-center">
              <span className="font-heading font-bold text-2xl text-primary block">SUUBI</span>
              <span className="text-muted-foreground text-sm">Community Development Organisation</span>
            </div>
          </div>

          <Card className="border-0 shadow-lg">
            <CardHeader className="space-y-1 text-center">
              <CardTitle className="font-heading text-2xl">Welcome back</CardTitle>
              <CardDescription>
                Enter your credentials to access the platform
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@suubi.org"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="login-email"
                    className="input-focus"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      data-testid="login-password"
                      className="input-focus pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <Button
                  type="submit"
                  className="w-full btn-press bg-primary hover:bg-primary/90"
                  disabled={loading}
                  data-testid="login-submit"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    'Sign in'
                  )}
                </Button>
              </form>

              <div className="mt-6 pt-6 border-t">
                <p className="text-sm text-muted-foreground text-center mb-3">
                  First time? Create demo data to explore
                </p>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={seedDemoData}
                  disabled={seeding}
                  data-testid="seed-demo-btn"
                >
                  {seeding ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating demo data...
                    </>
                  ) : (
                    'Load Demo Data'
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          <p className="text-center text-sm text-muted-foreground mt-6">
            Suubi Community Development Organisation
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
